import React, { ReactElement, useEffect, useState } from "react"
import Select, { SingleValue } from "react-select"

import { faCheck, faLongArrowAltLeft, faPlus, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button } from "@wfp/ui"

import { FormulaField, MappingField } from "interfaces/mappingInterfaces"
import OverflowText from "components/Shared/OverflowText/OverflowText"

type Option = { label: string; value: string }

interface Props {
  onAddAction: () => void
  onRemoveAction: (calculatedIndex: number) => void
  onConfirmAction: () => void
  onAddFormula: (value: string) => void
  field: MappingField
}

const options: Option[] = [
  { value: "sum", label: "Sum" },
  { value: "sub", label: "Subtraction" },
  { value: "mul", label: "Multiplication" },
  { value: "div", label: "Division" },
  { value: "conc", label: "Concatenation" },
]

const XlsFormCalculated = ({
  field,
  onAddAction,
  onRemoveAction,
  onConfirmAction,
  onAddFormula,
}: Props): ReactElement => {
  const [operation, setOperation] = useState<Option | null>(
    field.formula
      ? {
          value: field.formula,
          label: options.find(opt => opt.value === field.formula)?.label || "",
        }
      : options[0],
  )
  const fields = field?.formulaFields || []

  const handleAddField = (): void => {
    onAddAction()
  }

  const handleRemoveField = (index: number): void => {
    onRemoveAction(index)
  }

  const handleConfirm = (): void => {
    onConfirmAction()
  }

  const handleFormula = (option: SingleValue<Option>): void => {
    setOperation(option)
    onAddFormula(option?.value || "")
  }

  useEffect(() => {
    operation && onAddFormula(operation.value)
  }, [])

  return (
    <div className="w-100">
      <div className="flex flex-wrap">
        <div className="w-2/6 pr-6">
          <Select
            className="select-wrapper"
            classNamePrefix="select"
            placeholder="Select operation"
            options={options || []}
            isSearchable
            value={operation}
            onChange={value => handleFormula(value)}
          />
        </div>

        <div className="w-4/6 flex justify-end">
          <div>
            {fields.map((item: FormulaField, i: number) => (
              <div key={`field-${i}`} className="mb-1 d-flex flex-row align-center">
                <div
                  key={`field-mapped-${i}`}
                  className="border border-sky-600 border-solid p-1 xl:min-w-[300px] min-w-[120px] w-full flex items-center "
                >
                  <FontAwesomeIcon icon={faLongArrowAltLeft} className="me-2" color="var(--support-02)" />
                  <div className="font-bold  text-sm">{item.fieldName}</div>
                  <div className="ms-2 max-w-[300px] text-xs table table-fixed w-full">
                    <div className="table-cell">
                      <OverflowText dark>{item.fieldLabel}</OverflowText>
                    </div>
                  </div>
                </div>
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="ms-5 cursor-pointer"
                  color="var(--support-01)"
                  size="lg"
                  onClick={() => handleRemoveField(i)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 mt-1">
        <Button onClick={handleAddField}>
          <FontAwesomeIcon icon={faPlus} className="me-2" size="sm" />
          Add
        </Button>
        <Button disabled={fields?.length < 2 || !operation?.value} onClick={handleConfirm}>
          <FontAwesomeIcon icon={faCheck} className="me-2" size="sm" />
          Confirm
        </Button>
      </div>
    </div>
  )
}

export default XlsFormCalculated
